export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('uk-UA');
};

/**
 * Форматує об'єкт Date у ISO рядок з локальним зсувом часової зони
 * Наприклад для UTC+3 о 00:30: new Date() → "2026-04-15T00:30:00+03:00"
 */
export const toLocalISOString = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const sign = offset <= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const ohh = String(Math.floor(abs / 60)).padStart(2, '0');
  const omm = String(abs % 60).padStart(2, '0');
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${mi}:${s}${sign}${ohh}:${omm}`;
};

/**
 * Отримує дату як YYYY-MM-DD з локальних компонентів Date (без UTC-зсуву)
 */
export const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Конвертує дату у форматі YYYY-MM-DD у ISO рядок з локальним зсувом часової зони
 * Наприклад для UTC+3: "2026-04-13" → "2026-04-13T00:00:00+03:00"
 * Можна вказати час для кінця дня: toUTCDateString(date, '23:59:59')
 */
export const toUTCDateString = (dateStr: string, time: string = '00:00:00'): string => {
  const offset = new Date().getTimezoneOffset();
  const sign = offset <= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${dateStr}T${time}${sign}${hh}:${mm}`;
};

/**
 * Парсить дату з ISO строки як локальну дату без урахування часу та UTC
 * Це вирішує проблему з UTC зсувом при роботі з датами
 */
export const parseDateOnlyLocal = (dateString: string): Date => {
  // Беремо лише дату з ISO строки (YYYY-MM-DD)
  const dateOnly = dateString.split('T')[0];
  // Парсимо як локальну дату (без часу)
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Форматує дату без урахування часу (використовує лише дату з ISO строки)
 * Це вирішує проблему з UTC зсувом при відображенні дат
 */
export const formatDateOnlyLocal = (dateString: string): string => {
  const date = parseDateOnlyLocal(dateString);
  return date.toLocaleDateString('uk-UA', { day: '2-digit', month: 'short' });
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('uk-UA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getBudgetTypeText = (type: string): string => {
  const types: Record<string, string> = { 
    'Weekly': 'Тижневий', 
    'Monthly': 'Місячний', 
    'Yearly': 'Річний', 
    'Event': 'Подія' 
  };
  return types[type] || 'Невідомо';
};

export const getRecurrenceTypeText = (type: number): string => {
  const types: Record<number, string> = { 
    1: 'Щотижня', 
    2: 'Щомісяця', 
    3: 'Щороку' 
  };
  return types[type] || 'Невідомо';
};

export const mapRecurrenceTypeFromBackend = (backendType: string | number): string => {
  if (typeof backendType === 'number') {
    return String(backendType);
  }
  
  const enumMapping: Record<string, string> = {
    'Weekly': '1',
    'Monthly': '2', 
    'Yearly': '3'
  };
  
  return enumMapping[backendType] || '2';
};

export const mapRecurrenceTypeToBackend = (frontendType: string): string => {
  const reverseMapping: Record<string, string> = {
    '1': 'Weekly',
    '2': 'Monthly',
    '3': 'Yearly'
  };
  
  return reverseMapping[frontendType] || 'Monthly';
};

/**
 * Форматує дату як YYYY-MM-DD (date-only формат)
 */
const formatDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Отримує дату понеділка для конкретного тижня (ISO 8601)
 */
const getMondayOfWeek = (year: number, weekNumber: number): Date => {
  // ISO 8601: тиждень 1 - це перший тиждень, що містить 4 січня
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7; // 1 (понеділок) до 7 (неділя)
  const daysToMonday = jan4Day - 1;
  const mondayOfJan4 = new Date(jan4);
  mondayOfJan4.setDate(jan4.getDate() - daysToMonday);
  
  const targetMonday = new Date(mondayOfJan4);
  targetMonday.setDate(mondayOfJan4.getDate() + (weekNumber - 1) * 7);
  
  return targetMonday;
};

/**
 * Отримує номер тижня та рік тижня з дати (ISO 8601)
 * Повертає об'єкт з номером тижня та роком (рік визначається четвером тижня)
 */
const getWeekNumberAndYear = (date: Date): { week: number; year: number } => {
  // Створюємо копію дати для роботи (локальна дата без часу)
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  // Допоміжна функція: UTC-мілісекунди для дати (уникає DST-зсувів при обчисленні різниці днів)
  const utcDays = (dt: Date) => Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const MS_PER_DAY = 86400000;
  
  // Знаходимо четвер тижня (ISO 8601: рік тижня визначається четвером)
  const dayNum = d.getDay() || 7; // 0 (неділя) -> 7, 1 (понеділок) -> 1, ..., 6 (субота) -> 6
  const daysToThursday = 4 - dayNum; // Скільки днів до четверга
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + daysToThursday);
  
  const year = thursday.getFullYear(); // Рік тижня - це рік четверга
  
  // Знаходимо понеділок першого тижня року (тиждень, що містить 4 січня)
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const daysToMondayOfJan4 = jan4Day - 1;
  const mondayOfWeek1 = new Date(year, 0, 4 - daysToMondayOfJan4);
  
  // Знаходимо понеділок поточного тижня
  const daysToMonday = dayNum === 0 ? 6 : dayNum - 1; // Якщо неділя, то 6 днів назад, інакше dayNum - 1
  const monday = new Date(d);
  monday.setDate(d.getDate() - daysToMonday);
  
  // Обчислюємо номер тижня (через UTC щоб DST не впливав)
  const daysDiff = Math.round((utcDays(monday) - utcDays(mondayOfWeek1)) / MS_PER_DAY);
  const weekNo = Math.floor(daysDiff / 7) + 1;
  
  // Якщо тиждень належить до попереднього року (може статися наприкінці року)
  if (weekNo < 1) {
    const prevYear = year - 1;
    const prevJan4 = new Date(prevYear, 0, 4);
    const prevJan4Day = prevJan4.getDay() || 7;
    const prevDaysToMonday = prevJan4Day - 1;
    const prevMondayOfWeek1 = new Date(prevYear, 0, 4 - prevDaysToMonday);
    const prevDaysDiff = Math.round((utcDays(monday) - utcDays(prevMondayOfWeek1)) / MS_PER_DAY);
    const prevWeekNo = Math.floor(prevDaysDiff / 7) + 1;
    
    if (prevWeekNo >= 1 && prevWeekNo <= 53) {
      return { week: prevWeekNo, year: prevYear };
    }
  }
  
  // Якщо тиждень належить до наступного року (може статися на початку року)
  if (weekNo > 52) {
    const nextYear = year + 1;
    const nextJan4 = new Date(nextYear, 0, 4);
    const nextJan4Day = nextJan4.getDay() || 7;
    const nextDaysToMonday = nextJan4Day - 1;
    const nextMondayOfWeek1 = new Date(nextYear, 0, 4 - nextDaysToMonday);
    
    // Перевіряємо чи понеділок поточного тижня дійсно належить до наступного року
    if (utcDays(monday) >= utcDays(nextMondayOfWeek1)) {
      return { week: 1, year: nextYear };
    }
  }
  
  return { week: weekNo, year };
};


/**
 * Парсить тиждень з формату YYYY-WW
 */
export const parseWeek = (weekString: string): { year: number; week: number } => {
  const [year, week] = weekString.split('-W').map(Number);
  return { year, week };
};

/**
 * Форматує тиждень у формат YYYY-WW
 */
export const formatWeek = (year: number, week: number): string => {
  return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Отримує поточний тиждень у форматі YYYY-WW
 */
export const getCurrentWeek = (): string => {
  const now = new Date();
  const { week, year } = getWeekNumberAndYear(now);
  return formatWeek(year, week);
};

/**
 * Отримує список тижнів на основі доступних дат з бекенду
 */
export const getAvailableWeeks = (availableDates: string[]): Array<{ value: string; label: string }> => {
  if (!availableDates || availableDates.length === 0) {
    return [];
  }
  
  const weeksMap = new Map<string, { monday: Date; sunday: Date }>();
  
  // Обробляємо всі дати та визначаємо тижні
  availableDates.forEach(dateStr => {
    if (!dateStr) return;
    
    try {
      // Використовуємо parseDateOnlyLocal для правильного парсингу дат без UTC зсуву
      const date = parseDateOnlyLocal(dateStr);
      
      // Перевіряємо чи дата валідна
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date string: ${dateStr}`);
        return;
      }
      
      const { week, year } = getWeekNumberAndYear(date);
      const weekKey = formatWeek(year, week);
      
      // Якщо тиждень ще не додано, обчислюємо понеділок та неділю
      if (!weeksMap.has(weekKey)) {
        const monday = getMondayOfWeek(year, week);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        weeksMap.set(weekKey, { monday, sunday });
        
        // Діагностичне логування (можна видалити після виправлення)
        if (availableDates.length <= 10) {
          console.log(`Date: ${dateStr} -> Week: ${weekKey} (${formatDateOnly(monday)} - ${formatDateOnly(sunday)})`);
        }
      }
    } catch (error) {
      console.warn(`Error processing date: ${dateStr}`, error);
    }
  });
  
  // Сортуємо від найновіших до найстаріших
  const weeks = Array.from(weeksMap.entries())
    .sort((a, b) => {
      // Порівнюємо по понеділку тижня
      return b[1].monday.getTime() - a[1].monday.getTime();
    })
    .map(([weekKey, { monday, sunday }]) => {
      const isCurrentWeek = weekKey === getCurrentWeek();
      const label = isCurrentWeek 
        ? `Поточний тиждень (${formatDateOnly(monday)} - ${formatDateOnly(sunday)})`
        : `${formatDateOnly(monday)} - ${formatDateOnly(sunday)}`;
      
      return {
        value: weekKey,
        label
      };
    });
  
  return weeks;
};

/**
 * Отримує список місяців на основі доступних дат з бекенду
 */
export const getAvailableMonths = (availableDates: string[], selectedYear?: number): Array<{ value: number; label: string }> => {
  const months = [
    'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
    'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'
  ];
  
  const monthSet = new Set<number>();
  
  availableDates.forEach(dateStr => {
    const date = new Date(dateStr);
    // Якщо вказано рік, фільтруємо тільки по ньому
    if (selectedYear === undefined || date.getFullYear() === selectedYear) {
      monthSet.add(date.getMonth());
    }
  });
  
  return Array.from(monthSet)
    .sort((a, b) => b - a) // Сортуємо від більшого до меншого (нові місяці спочатку)
    .map(monthIndex => ({
      value: monthIndex,
      label: months[monthIndex]
    }));
};

/**
 * Отримує список років на основі доступних дат з бекенду
 */
export const getAvailableYears = (availableDates: string[]): Array<{ value: number; label: string }> => {
  const yearSet = new Set<number>();
  
  availableDates.forEach(dateStr => {
    const date = new Date(dateStr);
    yearSet.add(date.getFullYear());
  });
  
  return Array.from(yearSet)
    .sort((a, b) => b - a) // Сортуємо від більшого до меншого (нові роки спочатку)
    .map(year => ({
      value: year,
      label: String(year)
    }));
};

export const getDateRange = (
  period: 'week' | 'month' | 'year',
  selectedMonth?: number,
  selectedYear?: number,
  selectedWeek?: string
): { dateFrom: string; dateTo: string } => {
  const now = new Date();
  let dateFrom: Date;
  let dateTo: Date;
  
  switch (period) {
    case 'week':
      if (selectedWeek) {
        const { year, week } = parseWeek(selectedWeek);
        dateFrom = getMondayOfWeek(year, week);
        dateTo = new Date(dateFrom);
        dateTo.setDate(dateFrom.getDate() + 6);
      } else {
        // Поточний тиждень - використовуємо правильний рік з ISO 8601
        const { week, year } = getWeekNumberAndYear(now);
        dateFrom = getMondayOfWeek(year, week);
        dateTo = new Date(dateFrom);
        dateTo.setDate(dateFrom.getDate() + 6);
        dateTo.setHours(23, 59, 59, 999);
      }
      break;
    case 'month':
      if (selectedMonth !== undefined && selectedYear !== undefined) {
        dateFrom = new Date(selectedYear, selectedMonth, 1);
        dateTo = new Date(selectedYear, selectedMonth + 1, 0); // Останній день місяця
      } else {
        // Поточний місяць
        dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
        dateTo = new Date(now);
      }
      break;
    case 'year':
      if (selectedYear !== undefined) {
        dateFrom = new Date(selectedYear, 0, 1);
        dateTo = new Date(selectedYear, 11, 31);
      } else {
        // Поточний рік
        dateFrom = new Date(now.getFullYear(), 0, 1);
        dateTo = new Date(now);
      }
      break;
    default:
      dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
      dateTo = new Date(now);
  }
  
  return {
    dateFrom: formatDateOnly(dateFrom),
    dateTo: formatDateOnly(dateTo)
  };
};
